// One function per endpoint, typed by the core contracts (docs/project.md §7–§8).
import {
  AuthCodeResponseSchema,
  AuthLogoutResponseSchema,
  AuthVerifyResponseSchema,
  DictionariesResponseSchema,
  DictionaryCreateResponseSchema,
  DictionaryLeaveResponseSchema,
  EntryCreateResponseSchema,
  EntryDeleteResponseSchema,
  EntryFillResponseSchema,
  EntryHeadwordResponseSchema,
  EntryUpdateResponseSchema,
  LookupResponseSchema,
  MePatchResponseSchema,
  MeResponseSchema,
  OnboardingResponseSchema,
  SessionDeleteResponseSchema,
  SessionsResponseSchema,
  ShareAcceptResponseSchema,
  ShareCreateResponseSchema,
  ShareLookupResponseSchema,
  ShareRevokeResponseSchema,
  SharesResponseSchema,
  SuggestResponseSchema,
  SyncResponseSchema,
  ViewsResponseSchema,
  type AuthVerifyRequest,
  type DictionaryCreateRequest,
  type EntryManualCreateRequest,
  type EntryUpdateRequest,
  type MePatchRequest,
  type OnboardingRequest,
  type ViewsRequest,
} from '@solungo/core/schema';
import { request } from './client';

const enc = encodeURIComponent;

export const api = {
  auth: {
    code: (email: string) =>
      request('/auth/code', { method: 'POST', body: { email }, schema: AuthCodeResponseSchema }),
    verify: (body: AuthVerifyRequest) =>
      request('/auth/verify', { method: 'POST', body, schema: AuthVerifyResponseSchema }),
    logout: () => request('/auth/logout', { method: 'POST', schema: AuthLogoutResponseSchema }),
  },
  me: {
    get: (signal?: AbortSignal) =>
      request('/me', { schema: MeResponseSchema, signal, notifyUnauthorized: false }),
    patch: (body: MePatchRequest) =>
      request('/me', { method: 'PATCH', body, schema: MePatchResponseSchema }),
    onboarding: (body: OnboardingRequest) =>
      request('/me/onboarding', { method: 'POST', body, schema: OnboardingResponseSchema }),
    sessions: () => request('/me/sessions', { schema: SessionsResponseSchema }),
    deleteSession: (id: string) =>
      request(`/me/sessions/${enc(id)}`, { method: 'DELETE', schema: SessionDeleteResponseSchema }),
  },
  dictionaries: {
    list: (signal?: AbortSignal) => request('/dictionaries', { schema: DictionariesResponseSchema, signal }),
    create: (body: DictionaryCreateRequest) =>
      request('/dictionaries', { method: 'POST', body, schema: DictionaryCreateResponseSchema }),
    shares: (id: string) => request(`/dictionaries/${enc(id)}/shares`, { schema: SharesResponseSchema }),
    invite: (id: string, email: string) =>
      request(`/dictionaries/${enc(id)}/shares`, {
        method: 'POST',
        body: { email },
        schema: ShareCreateResponseSchema,
      }),
    revoke: (id: string, shareId: string) =>
      request(`/dictionaries/${enc(id)}/shares/${enc(shareId)}`, {
        method: 'DELETE',
        schema: ShareRevokeResponseSchema,
      }),
    leave: (id: string) =>
      request(`/dictionaries/${enc(id)}/leave`, { method: 'POST', schema: DictionaryLeaveResponseSchema }),
    views: (id: string, body: ViewsRequest) =>
      request(`/dictionaries/${enc(id)}/views`, { method: 'POST', body, schema: ViewsResponseSchema }),
  },
  shares: {
    lookup: (token: string) => request(`/shares/${enc(token)}`, { schema: ShareLookupResponseSchema }),
    accept: (token: string) =>
      request(`/shares/${enc(token)}/accept`, { method: 'POST', schema: ShareAcceptResponseSchema }),
  },
  entries: {
    // The request is short (no model call on the server); a hung connection must not leave a
    // spinner row forever, so it is abandoned after a while and retried
    add: (dictionaryId: string, headword: string) =>
      request(`/dictionaries/${enc(dictionaryId)}/entries`, {
        method: 'POST',
        body: { headword },
        schema: EntryCreateResponseSchema,
        signal: AbortSignal.timeout(20_000),
      }),
    fill: (id: string) =>
      request(`/entries/${enc(id)}/fill`, { method: 'POST', schema: EntryFillResponseSchema }),
    addManual: (dictionaryId: string, body: EntryManualCreateRequest) =>
      request(`/dictionaries/${enc(dictionaryId)}/entries/manual`, {
        method: 'POST',
        body,
        schema: EntryCreateResponseSchema,
      }),
    update: (id: string, body: EntryUpdateRequest) =>
      request(`/entries/${enc(id)}`, { method: 'PUT', body, schema: EntryUpdateResponseSchema }),
    headword: (id: string, headword: string) =>
      request(`/entries/${enc(id)}/headword`, {
        method: 'PUT',
        body: { headword },
        schema: EntryHeadwordResponseSchema,
      }),
    remove: (id: string) =>
      request(`/entries/${enc(id)}`, { method: 'DELETE', schema: EntryDeleteResponseSchema }),
    // The card of a related word before it is added: read-only (docs/project.md §7)
    lookup: (dictionaryId: string, headword: string, signal?: AbortSignal) =>
      request(`/dictionaries/${enc(dictionaryId)}/lookup?headword=${enc(headword)}`, {
        schema: LookupResponseSchema,
        signal,
      }),
    // Words of the shared base for the omnibox query, beside the local search (docs/project.md §7)
    suggest: (dictionaryId: string, q: string, signal?: AbortSignal) =>
      request(`/dictionaries/${enc(dictionaryId)}/suggest?q=${enc(q)}`, {
        schema: SuggestResponseSchema,
        signal,
      }),
  },
  sync: {
    // The puller bounds the request itself (PULL_TIMEOUT_MS): a first page of 500 entries on a
    // slow network may take longer than the default GET limit
    pull: (dictionaryId: string, since: string, signal?: AbortSignal) =>
      request(`/sync/${enc(dictionaryId)}?since=${enc(since)}`, { schema: SyncResponseSchema, signal, timeout: false }),
  },
};
